import { DispatcherV2Request, DispatcherV2Response, ResponseItem, Task } from '@bringg/types';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { getRoot } from 'mobx-easy';

import RootStore from 'bringg-web/stores/root-store';
import { fetchFieldsProvider } from '../../shared/utils/fetch-task-fields/fetch-fields-provider';
import { fetchPlannedRoutes } from '../../shared/utils/fetch-planned-routes';

const CHUNK_SIZE = 150;

const load = async (
	request: DispatcherV2Request,
	options: {
		signal?: AbortSignal;
	} = {}
): Promise<DispatcherV2Response> => {
	const response = await getRootEnv().dashboardSdk.sdk.v2.tasksExplorer.getAll(request, { signal: options.signal });
	const tasks = await fetchItems(response.query_responses[0].items_by_group);

	if (tasks) {
		await Promise.all([
			fetchCustomersByTasks(tasks, options),
			fetchRuns(tasks, options),
			fetchDrivers(tasks, options)
		]);
	}

	return response;
};

const fetchItems = async (items: Record<string, ResponseItem[]>): Promise<Task[] | undefined> => {
	const ids = Object.values(items).reduce((acc: number[], itemsByGroup) => {
		itemsByGroup.forEach(item => {
			acc.push(item.id);
		});
		return acc;
	}, []);

	return await fetchTasks(ids);
};

const fetchTasks = async (ids: number[]): Promise<Task[] | undefined> => {
	if (!ids.length) {
		return;
	}

	return await getRootEnv().dashboardSdk.sdk.v2.tasks.loadMany(ids as unknown as number[], fetchFieldsProvider.get());
};

const fetchCustomersByTasks = async (tasks: Task[], options: { signal?: AbortSignal }) => {
	const customerIdsToFetch = new Set<number>();

	tasks.forEach(task => {
		const { way_points } = task;

		if (way_points) {
			const last = way_points[way_points.length - 1];
			last.customer_id && customerIdsToFetch.add(last.customer_id);
		}
	});

	if (customerIdsToFetch.size) {
		await getRootEnv().dashboardSdk.sdk.v2.customers.loadMany(Array.from(customerIdsToFetch.values()), {
			useCache: true,
			chunkSize: CHUNK_SIZE,
			signal: options.signal
		});
	}
};

const fetchRuns = async (tasks: Task[], options: { signal?: AbortSignal }) => {
	const runIds = new Set<number>(tasks.map(task => task.run_id).filter(Boolean));

	if (runIds.size) {
		const routes = await getRoot<RootStore>().data.runStore.loadMany({ ids: Array.from(runIds) }, undefined, {
			signal: options.signal
		});
		fetchPlannedRoutes(routes);
	}
};

async function fetchDrivers(tasks: Task[], options: { signal?: AbortSignal }) {
	const userIds = new Set<number>(tasks.map(task => task.user_id).filter(Boolean));

	if (userIds.size) {
		await getRoot<RootStore>().data.driversStore.loadMany(Array.from(userIds), {
			signal: options.signal
		});
	}
}

export const orderExplorerLoader = { load };
